import { Toast } from '@/components/molecules/Toast';
import { PaymentResultScreen } from '@/components/organisms';
import { useTranslation } from '@/core/hooks';
import { logger } from '@/core/logger';
import * as Clipboard from 'expo-clipboard';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';

/**
 * Unified payment redirect screen that handles all payment statuses
 * Supports query parameters: ?status=success|authorised|failed|failure|cancel&orderRef=...
 * 
 * URL examples:
 * - https://bloomone.bloomholding.com/payment-redirect?status=success&orderRef=...
 * - https://bloomone.bloomholding.com/payment-redirect?status=failed&orderRef=...
 */
export default function PaymentRedirectScreen() {
    const { t } = useTranslation();
    const params = useLocalSearchParams<{ status?: string; orderRef?: string; OrderRef?: string; unitId?: string; unitLabel?: string }>();
    const status = params.status || 'failed'; // Default to failed if no status
    // Support both 'orderRef' (lowercase) and 'OrderRef' (capital O) from payment gateway
    let orderRef = params.orderRef || params.OrderRef || undefined;
    
    // On web, if orderRef is not in URL params, get it from localStorage (stored before redirect)
    if (!orderRef && typeof window !== 'undefined') {
        try {
            const storedOrderRef = localStorage.getItem('payment.orderRef');
            if (storedOrderRef) {
                orderRef = storedOrderRef;
            }
        } catch (error) {
            // Silently fail if localStorage is not available
        }
    }
    
    // Log payment redirect parameters for debugging
    logger.debug('PaymentRedirectScreen:Payment redirect parameters', {
        status,
        orderRef,
        hasOrderRef: !!orderRef,
        allParams: params,
    });
    const unitId = params.unitId || undefined;
    const unitLabel = params.unitLabel || undefined;
    
    // Toast state for copy feedback
    const [toastVisible, setToastVisible] = useState(false);
    
    // Determine type from status query param
    // Support 'success'/'authorised' for success, 'cancel'/'cancelled' for cancelled, and 'failed'/'failure' for failed
    const isSuccess = status === 'success' || status === 'authorised';
    const isCancel = status === 'cancel' || status === 'cancelled';
    
    let type: 'success' | 'failed' | 'cancel';
    if (isSuccess) {
        type = 'success';
    } else if (isCancel) {
        type = 'cancel';
    } else {
        type = 'failed';
    }

    // Note: Unit installments refresh happens in PaymentResultScreen component
    // It only triggers after payment status API confirms status.status === 'SUCCESS'
    // This ensures we don't refresh unnecessarily before payment is confirmed

    // Handle "Go to home" - clear all screens and replace to home
    const handleBackToHome = () => {
        router.dismissAll();
        router.replace('/(auth)/(home)');
    };

    // Handle retry - go back to payment summary to retry checkout
    const handleRetry = () => {
        // Navigate back to payment summary screen
        router.back();
    };

    // Handle "Raise cheque shred request" - navigate to support screen (for success case)
    const handleRaiseChequeShredRequest = () => {
        router.push({
            pathname: '/service-request-create',
            params: {
                categoryName: 'Replace My Cheque',
                description: 'Raise cheque shred request',
                unitId: unitId || '',
                unitLabel: unitLabel || '',
            },
        } as any);
    };

    // Handle "Report an issue" - navigate to support screen
    const handleReportIssue = () => {
        router.push('/service-request' as any);
    };

    // Handle "Copy transaction ID" - copy to clipboard
    const handleCopyTransactionId = async (transactionId: string) => {
        try {
            await Clipboard.setStringAsync(transactionId);
            logger.info('PaymentRedirectScreen', 'Transaction ID copied to clipboard', { transactionId });
            // Show toast notification
            setToastVisible(true);
        } catch (error) {
            logger.error('PaymentRedirectScreen', 'Failed to copy transaction ID', error);
        }
    };
    
    return (
        <>
            <PaymentResultScreen
                type={type}
                {...(orderRef ? { orderRef } : {})}
                onSecondaryActionPress={handleBackToHome}
                onPrimaryActionPress={
                    type === 'failed' 
                        ? handleRetry 
                        : type === 'cancel'
                            ? handleBackToHome
                            : undefined
                }
                onReportIssuePress={handleReportIssue}
                onCopyTransactionIdPress={handleCopyTransactionId}
            />
            <Toast
                visible={toastVisible}
                message={t('payments.transactionIdCopied')}
                onHide={() => setToastVisible(false)}
            />
        </>
    );
}

